import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import _ from 'lodash';
import l from '../../../../../lang';
import './styles.scss';
import {
  getCurrencies,
  postCreatePaymentLink,
  getDocumentTypesForPaymentLink,
  paymentLinkResult,
} from '../../../../../store/payment-link/actions';

import { commons as validations } from '../../../../../utils/validations';
import { allCountries } from '../../../../../config';
import { scrollIntoView } from '../../../../../utils';
import PaymentLinkDialog from './payment-link-dialog';
import CreateFormFields from './create-form-fields';

const CreatePaymentLinkForm = ({
  loading,
  error,
  errorInfo,
  errorFields,
  currencies,
  canOperate,
  fraudReview,
  fnGetCurrencies,
  fnGetDocumentTypes,
  fnCreatePaymenyLink,
  showResult,
  createSuccess,
  fnShowResult,
}) => {
  const [showClientData, setShowClientData] = useState(false);
  const [newClient, setNewClient] = useState(true);

  const errorCardRef = useRef(null);

  const countryRef = useRef(null);
  const currencyRef = useRef(null);
  const amountRef = useRef(null);
  const clientEmailRef = useRef(null);
  const sendEmailRef = useRef(null);
  const clientDocumentRef = useRef(null);

  const scrollToFormErrors = errors => {
    if (errors.country) {
      scrollIntoView(countryRef);
    } else if (errors.currency) {
      scrollIntoView(currencyRef);
    } else if (errors.amount) {
      scrollIntoView(amountRef);
    } else if (errors.clientEmail) {
      scrollIntoView(clientEmailRef);
    } else if (errors.sendEmail) {
      scrollIntoView(sendEmailRef);
    } else if (errors.clientDocument) {
      scrollIntoView(clientDocumentRef);
    }
  };

  useEffect(() => {
    if (error) {
      scrollIntoView(errorCardRef);
    }
  }, [error]);

  const initialValues = {
    country: '',
    currency: '',
    amount: 0,
    clientEmail: '',
    sendEmail: false,
    clientFirstName: '',
    clientLastName: '',
    clientDocumentType: '',
    clientDocument: '',
    description: '',

    clientSelected: null,
  };

  const validate = values => {
    const errors = {};
    if (!values.country) {
      errors.country = l('form.input.required');
    } else if (errorFields?.country === values.country && !_.isEmpty(errorFields?.country) && errorInfo?.message) {
      errors.country = l(errorInfo.message);
    }
    if (!values.currency) {
      errors.currency = l('form.input.required');
    }
    if (values.amount <= 0) {
      errors.amount = l('paymentLink.amount.error');
    } else if (errorFields?.amount === values.amount && errorInfo?.message && errorInfo.messageArgs) {
      errors.amount = l(errorInfo.message, ...errorInfo.messageArgs);
    }
    if (errorFields?.clientDocument === values.clientDocument && !_.isEmpty(errorFields?.clientDocument)) {
      errors.clientDocument = l('paymentLink.document.error');
    }

    function getEmailErrors({ clientEmail, sendEmail, clientSelected }, showClientData, newClient) {
      const emailErrors = {};
      if (sendEmail && (!showClientData || newClient) && !clientEmail) {
        emailErrors.clientEmail = l('form.input.required');
      }
      if ((!showClientData || newClient) && clientEmail && !validations.emailValid(clientEmail)) {
        emailErrors.clientEmail = l('form.email.invalid');
      }
      if (sendEmail && showClientData && !newClient) {
        if (!clientSelected) {
          emailErrors.clientSelected = l('paymentLink.existingClientError');
        }
        if (clientSelected && !clientSelected.email) {
          emailErrors.sendEmail = l('paymentLink.sendEmailError');
        }
      }
      return emailErrors;
    }
    const emailErrors = getEmailErrors(values, showClientData, newClient);

    return { ...errors, ...emailErrors };
  };

  const countries = () => {
    const countries = {};
    allCountries.forEach(co => {
      countries[co] = l(`countryCode.${co}`);
    });
    return countries;
  };



  const handleSubmit = values => {
    const valuesToSend = {};
    Object.keys(values).forEach(key => {
      if (typeof values[key] === 'string') {
        if (values[key].trim() !== '') {
          valuesToSend[key] = values[key];
        }
      } else {
        valuesToSend[key] = values[key];
      }
    });

    const { country, currency, amount, description, clientEmail, sendEmail } = valuesToSend;
    const commonValues = {
      country,
      currency,
      amount,
      description,
      clientEmail,
      sendEmail,
    };

    if (showClientData) {
      if (newClient) {
        const { clientFirstName, clientLastName, clientDocumentType, clientDocument } = valuesToSend;
        fnCreatePaymenyLink({
          ...commonValues,
          clientFirstName,
          clientLastName,
          clientDocumentType,
          clientDocument,
        });
      } else {
        const { clientSelected } = valuesToSend;
        fnCreatePaymenyLink({
          ...commonValues,
          merchantClientId: clientSelected.id,
        });
      }
    } else {
      fnCreatePaymenyLink(commonValues);
    }
  };

  const formDisabled = !canOperate || fraudReview || loading;

  useEffect(() => {
    if (createSuccess) {
      fnShowResult(true);
    }
  }, [fnShowResult, createSuccess]);

  return (
    <>
      <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
        {formikProps => {
          const { values, setFieldValue, resetForm } = formikProps;
          return (
            <>
              <CreateFormFields
                scrollToFormErrors={scrollToFormErrors}
                setShowClientData={setShowClientData}
                setNewClient={setNewClient}
                errorCardRef={errorCardRef}
                countryRef={countryRef}
                amountRef={amountRef}
                currencyRef={currencyRef}
                clientEmailRef={clientEmailRef}
                clientDocumentRef={clientDocumentRef}
                countries={countries}
                formDisabled={formDisabled}
                showClientData={showClientData}
                newClient={newClient}
                canOperate={canOperate}
                fraudReview={fraudReview}
                fnGetCurrencies={fnGetCurrencies}
                fnGetDocumentTypes={fnGetDocumentTypes}
                {...formikProps}
              />

              {showResult && (
                <PaymentLinkDialog
                  url={createSuccess?.url ?? ''}
                  country={values.country}
                  currency={values.currency}
                  amount={values.amount}
                  description={values.description}
                  onClose={() => {
                    resetForm();
                    fnShowResult(false);
                  }}
                />
              )}
            </>
          );
        }}
      </Formik>
    </>
  );
};

CreatePaymentLinkForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  errorInfo: PropTypes.object,
  currencies: PropTypes.object,
  documentTypes: PropTypes.object,
  canOperate: PropTypes.bool,
  fraudReview: PropTypes.bool,
  creditCardEnabled: PropTypes.bool,
  maxTransactionAmountCc: PropTypes.number,
  fnGetCurrencies: PropTypes.func,
  fnGetDocumentTypes: PropTypes.func,
  fnCreatePaymenyLink: PropTypes.func,
  createSuccess: PropTypes.object,
  showResult: PropTypes.bool,
  fnShowResult: PropTypes.func,
};

const mapStateToProps = state => ({
  loading: state.paymentLink.loading,
  error: state.paymentLink.error,
  errorInfo: state.paymentLink.errorInfo,
  errorFields: state.paymentLink.errorFields,
  currencies: state.paymentLink.currencies,
  documentTypes: state.paymentLink.documentTypes,
  canOperate: state.dashboard.merchantMe?.canOperate,
  fraudReview: state.dashboard.merchantMe?.fraudReview,
  creditCardEnabled: state.dashboard.merchantMe?.kycLevel.creditCardEnabled,
  maxTransactionAmountCc: state.dashboard.merchantMe?.kycLevel.maxTransactionAmountCc,
  createSuccess: state.paymentLink.createSuccess,
  showResult: state.paymentLink.showResult,
});

const mapDispatchToProps = dispatch => ({
  fnGetCurrencies: country => dispatch(getCurrencies(country)),
  fnGetDocumentTypes: country => dispatch(getDocumentTypesForPaymentLink(country)),
  fnCreatePaymenyLink: values => dispatch(postCreatePaymentLink(values)),
  fnShowResult: visible => dispatch(paymentLinkResult(visible)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreatePaymentLinkForm);
